import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, X, ShoppingBag, MapPin, User, LogIn, Plus, Star, Search } from 'lucide-react';
import api from '@/lib/api';
import OrderTestConsole from '@/components/OrderTestConsole';
import AddressSearch from '@/components/AddressSearch';
import ReturnToLiveButton from '@/components/ReturnToLiveButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const won = (n) => `${(n || 0).toLocaleString('ko-KR')}원`;

// ---- Phase 9 · Consumer checkout for a seller_cart (login-gated in P13-B) --
export default function Phase9Checkout() {
  const { cartId } = useParams();
  const [state, setState] = useState({ loading: true, cart: null, seller: null, error: '' });
  const [me, setMe] = useState(null);          // Phase 13-B: logged-in LIVEMOA user
  const [authChecked, setAuthChecked] = useState(false);
  const [form, setForm] = useState({
    buyer_name: '', buyer_phone: '', postal_code: '',
    address1: '', address2: '', memo: '', email: '',
  });
  // Address book (Phase 13 후속). members can pick a saved address in one tap.
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [manualEntry, setManualEntry] = useState(false); // members: 새 배송지 직접 입력 여부
  const [saveAsAddress, setSaveAsAddress] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const isPrivileged = !!me && (me.is_admin === true || me.role === 'admin' || me.role === 'super_admin' || me.seller_status === 'approved');
  // Ref for detail input so Daum Postcode can focus it after search.
  const detailInputRef = useRef(null);

  // Applied by the Daum Postcode picker — fills postal + road address so users
  // only type the detail (동/호수) themselves. Mirrors the Checkout.js pattern.
  const applyPostcode = (r) => {
    const combined = (r.base_address + (r.extra_address ? ' ' + r.extra_address : '')).trim();
    setForm((f) => ({
      ...f,
      postal_code: r.postal_code || '',
      address1: combined || f.address1,
    }));
    // If the shopper was viewing a saved-address radio, drop the selection —
    // they are now creating a new entry.
    setSelectedAddressId('');
    setManualEntry(true);
  };

  useEffect(() => {
    // Phase 13-B: check LIVEMOA session first. Consumers must log in via Kakao
    // before submitting a comment-order Checkout. Existing Guest test data is
    // preserved — we simply block NEW submissions from the consumer UI.
    api.get('/auth/me')
      .then((r) => { setMe(r.data); })
      .catch(() => { setMe(null); })
      .finally(() => setAuthChecked(true));

    axios.get(`${API}/public/seller-checkout/${cartId}`)
      .then((r) => {
        setState({ loading: false, cart: r.data.cart, seller: r.data.seller, error: '' });
        const c = r.data.cart;
        if (c?.buyer_name) setForm((f) => ({ ...f, buyer_name: c.buyer_name }));
        if (c?.order_id) setDone({
          order_id: c.order_id, order_number: c.order_number,
          message: '주문이 이미 접수되었습니다.',
        });
      })
      .catch((e) => setState({
        loading: false, cart: null, seller: null,
        error: e?.response?.data?.detail || '주문 정보를 불러올 수 없습니다.',
      }));
  }, [cartId]);

  // Once auth is known and cart is loaded, auto-fill buyer info from the
  // LIVEMOA profile so members don't retype their own name/phone/email.
  // Phase 13-B spec: 회원 프로필값을 최우선으로. cart.buyer_name 은 게스트
  // 폴백일 뿐 회원 로그인 시 override 하지 않으면 YouTube 핸들이 그대로
  // 노출되는 문제가 있어(iter25 P2), 회원이면 반드시 me.name 을 사용한다.
  useEffect(() => {
    if (!me) return;
    setForm((f) => ({
      ...f,
      buyer_name: me.name || f.buyer_name || '',
      buyer_phone: me.phone || f.buyer_phone || '',
      email: me.email || f.email || '',
    }));
    // Address book auto-load — pick the default address as one-tap fill.
    api.get('/buyer/addresses').then((r) => {
      const list = r.data || [];
      setAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setSelectedAddressId(def.address_id);
        applyAddress(def);
      } else {
        // No saved address → let the shopper type once and offer to save it.
        setManualEntry(true);
        setSaveAsAddress(true);
      }
    }).catch(() => { setManualEntry(true); });
  }, [me]);

  // Copy a saved address into the form so submit() can go through unchanged.
  const applyAddress = (a) => {
    if (!a) return;
    setForm((f) => ({
      ...f,
      buyer_name: a.recipient_name || f.buyer_name,
      buyer_phone: a.phone || f.buyer_phone,
      postal_code: a.postal_code || '',
      address1: a.address1 || '',
      address2: a.address2 || '',
      memo: a.memo || f.memo,
    }));
  };
  const pickAddress = (a) => {
    setSelectedAddressId(a.address_id);
    setManualEntry(false);
    setSaveAsAddress(false);
    applyAddress(a);
  };
  const startManualEntry = () => {
    setSelectedAddressId('');
    setManualEntry(true);
    setSaveAsAddress(addresses.length === 0);
    setForm((f) => ({ ...f, postal_code: '', address1: '', address2: '', memo: '' }));
  };

  const submit = async () => {
    if (!form.buyer_name.trim() || !form.buyer_phone.trim() || !form.address1.trim()) {
      toast.error('이름, 연락처, 배송지는 필수입니다.'); return;
    }
    setSubmitting(true);
    try {
      // Save-as-new-address (opt-in, members only). Fire and forget — do NOT
      // block order submit if the address save fails.
      if (me && manualEntry && saveAsAddress && form.postal_code && form.address1) {
        try {
          const list = await api.post('/buyer/addresses', {
            label: '주문 배송지', recipient_name: form.buyer_name, phone: form.buyer_phone,
            postal_code: form.postal_code, address1: form.address1, address2: form.address2,
            memo: form.memo, is_default: addresses.length === 0,
          });
          if (list?.data?.address_id) toast.success('배송지가 주소록에 저장되었습니다.');
        } catch { /* silent */ }
      }
      // withCredentials so the LIVEMOA session cookie is forwarded — the
      // backend then auto-links buyer_user_id.
      const r = await axios.post(`${API}/public/seller-checkout/${cartId}/submit`, form, {
        withCredentials: true,
      });
      setDone(r.data);
      toast.success('주문이 접수되었습니다.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '주문 접수 실패');
    } finally { setSubmitting(false); }
  };

  const cancel = async () => {
    if (!window.confirm('주문을 취소하시겠습니까?')) return;
    try {
      await axios.post(`${API}/public/seller-checkout/${cartId}/cancel`);
      toast.info('주문이 취소되었습니다.');
      setState((s) => ({ ...s, cart: { ...s.cart, status: 'cancelled' } }));
    } catch (e) { toast.error(e?.response?.data?.detail || '취소 실패'); }
  };

  const startKakao = () => {
    const back = encodeURIComponent(`/checkout/${cartId}`);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/kakao/start?return_url=${back}`;
  };

  if (state.loading || !authChecked) return <div className="p-8 text-center text-sm text-gray-500">불러오는 중…</div>;
  if (state.error) return <div className="p-8 text-center text-sm text-red-500" data-testid="p9-checkout-error">{state.error}</div>;

  const c = state.cart;

  if (done) {
    return (
      <div className="max-w-md mx-auto p-6 pt-16 space-y-4" data-testid="p9-checkout-done">
        <div className="text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
          <h1 className="text-xl font-black mb-2">주문 접수 완료</h1>
          <p className="text-sm text-gray-500 mb-1">주문번호</p>
          <p className="text-lg font-black mb-4" data-testid="p9-done-order-number">{done.order_number}</p>
          <p className="text-xs text-gray-500 whitespace-pre-line mb-6">{done.message}</p>
          {/* STEP 8-I-4 · 주문 완료 후 방송 복귀 CTA */}
          <div className="flex justify-center mb-2">
            <ReturnToLiveButton clearOnClick={true} />
          </div>
        </div>
        {isPrivileged && done.order_id && <OrderTestConsole orderId={done.order_id} />}
        <PublicOrderStatusMini orderId={done.order_id} />
      </div>
    );
  }

  if (c.status === 'cancelled') return (
    <div className="max-w-md mx-auto p-6 pt-16 text-center" data-testid="p9-checkout-cancelled">
      <X size={40} className="mx-auto text-gray-400 mb-2" />
      <p className="font-bold">취소된 주문입니다.</p>
    </div>
  );
  if (c.status === 'submitted') return (
    <div className="max-w-md mx-auto p-6 pt-16 text-center" data-testid="p9-checkout-submitted">
      <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-2" />
      <p className="font-bold">이미 접수된 주문입니다.</p>
      <p className="text-xs text-gray-500 mt-2">주문번호 {c.order_number}</p>
    </div>
  );

  // Phase 13-B: login-gate the consumer checkout. The cart itself stays intact
  // in the DB so the shopper picks up right where they left off after Kakao
  // login completes and redirects back to /checkout/{cartId}.
  if (!me) {
    return (
      <div className="max-w-md mx-auto p-6 pt-16 space-y-4 text-center" data-testid="p9-login-gate">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#FEE500] inline-flex items-center justify-center">
          <LogIn size={22} className="text-black" />
        </div>
        <h1 className="text-xl font-black">구매하려면 로그인이 필요합니다</h1>
        <p className="text-xs text-gray-500 leading-relaxed">
          {state.seller?.brand_name || state.seller?.name || '판매자'} 님의 주문을 이어서 진행하려면 LIVEMOA 로그인이 필요해요.
          로그인 후 이 페이지로 자동으로 돌아옵니다.
        </p>
        <button
          onClick={startKakao}
          data-testid="p9-kakao-login"
          className="w-full py-3 rounded-full bg-[#FEE500] hover:bg-[#F5D900] text-black text-sm font-black inline-flex items-center justify-center gap-2"
        >
          <ShoppingBag size={14} /> 카카오로 시작하기
        </button>
        <p className="text-[10px] text-gray-400">장바구니 · 상품 정보는 그대로 유지됩니다.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-4 pb-16 space-y-4" data-testid="p9-checkout-page">
      <div className="flex items-center justify-between pt-4">
        <div className="flex items-center gap-2">
          <ShoppingBag size={16} className="text-[#FF3B30]" />
          <h1 className="text-lg font-black">주문 확인</h1>
          {(c.source?.startsWith('youtube') || c.source?.startsWith('tiktok')) && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                c.source.startsWith('tiktok')
                  ? 'text-white bg-black border border-pink-400/60'
                  : 'text-red-500 bg-red-500/10'
              }`}
              data-testid="p9-source-badge"
            >
              {c.source.startsWith('tiktok') ? 'TikTok LIVE' : 'YouTube LIVE'}
            </span>
          )}
        </div>
        {/* STEP 8-I-4 · Return-to-LIVE CTA */}
        <ReturnToLiveButton />
      </div>
      <p className="text-[11px] text-gray-500">
        {state.seller?.brand_name || state.seller?.name || '판매자'} 님이 준비한 주문입니다. 아래 정보를 확인하고 배송지를 입력해주세요.
      </p>

      {/* Cart items */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid="p9-cart-items">
        {c.items.map((it, i) => (
          <div key={i} className="flex gap-3 items-center">
            {it.image && <img src={it.image} alt="" className="w-14 h-14 rounded object-cover" />}
            <div className="flex-1 min-w-0 text-xs">
              <p className="font-bold truncate">{it.product_name}</p>
              <p className="text-gray-500">
                {Object.entries(it.options || {}).map(([, v]) => v).join(' / ') || '옵션 없음'} · {it.quantity}개
              </p>
              <p className="font-bold text-[#FF3B30]">{won(it.subtotal)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Price summary */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 text-xs space-y-1" data-testid="p9-price-summary">
        <div className="flex justify-between"><span className="text-gray-500">상품 금액</span><span>{won(c.subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">배송비</span><span>{won(c.shipping_fee)}</span></div>
        <div className="flex justify-between font-black text-base pt-1 border-t border-black/10 dark:border-white/10 mt-1">
          <span>총 결제 금액</span><span className="text-[#FF3B30]" data-testid="p9-total">{won(c.total)}</span>
        </div>
      </div>

      {/* Buyer form — auto-filled from LIVEMOA profile, editable */}
      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2">
        <p className="text-xs font-black inline-flex items-center gap-1"><User size={11} /> 주문자 정보</p>
        {me && (
          <p className="text-[10px] text-gray-500" data-testid="p9-buyer-hint">
            LIVEMOA 회원 <b className="text-gray-700 dark:text-gray-300">{me.name || me.email || me.user_id}</b> 님의 정보가 자동 입력되었습니다.
          </p>
        )}
        <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
          placeholder="받는 사람 이름 *" data-testid="p9-input-name"
          className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
        <input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })}
          placeholder="연락처 (예: 010-1234-5678) *" data-testid="p9-input-phone" inputMode="tel"
          className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="이메일 (선택 · 주문 확인용)" data-testid="p9-input-email" type="email"
          className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-black inline-flex items-center gap-1"><MapPin size={11} /> 배송지</p>
          <Link to="/addresses" className="text-[10px] text-[#FF3B30] font-bold" data-testid="p9-manage-addresses">주소록 관리 →</Link>
        </div>

        {addresses.length > 0 && (
          <div className="space-y-1.5" data-testid="p9-address-book">
            {addresses.map((a) => (
              <label
                key={a.address_id}
                onClick={() => pickAddress(a)}
                className={`block cursor-pointer rounded-xl border p-2.5 transition-colors ${selectedAddressId === a.address_id && !manualEntry ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}
                data-testid={`p9-address-${a.address_id}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="p9-addr"
                    className="mt-0.5"
                    checked={selectedAddressId === a.address_id && !manualEntry}
                    onChange={() => pickAddress(a)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-bold text-[12px] truncate">{a.label || '배송지'}</p>
                      {a.is_default && (
                        <span className="text-[9px] font-black text-[#FF3B30] uppercase tracking-widest inline-flex items-center gap-0.5">
                          <Star size={9} className="fill-[#FF3B30]" /> 기본
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-500 truncate">{a.recipient_name} · {a.phone}</p>
                    <p className="text-[10px] text-gray-500 truncate">[{a.postal_code}] {a.address1} {a.address2}</p>
                  </div>
                </div>
              </label>
            ))}
            <button
              type="button"
              onClick={startManualEntry}
              className={`w-full flex items-center justify-center gap-1 py-2 rounded-xl border border-dashed text-[11px] font-bold ${manualEntry ? 'border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/15 dark:border-white/15 text-gray-500'}`}
              data-testid="p9-address-manual-toggle"
            >
              <Plus size={11} /> 새 배송지 직접 입력
            </button>
          </div>
        )}

        {(manualEntry || addresses.length === 0) && (
          <div className="space-y-2 pt-1" data-testid="p9-address-manual-form">
            <div className="flex gap-2">
              <input value={form.postal_code} readOnly
                placeholder="우편번호 (주소검색으로 자동 입력)" data-testid="p9-input-postal"
                className="flex-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
              <AddressSearch onComplete={applyPostcode} detailInputRef={detailInputRef} testId="p9-address-search-btn" />
            </div>
            <input value={form.address1} readOnly
              placeholder="주소 (주소검색으로 자동 입력) *" data-testid="p9-input-address1"
              className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
            <input ref={detailInputRef} value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })}
              placeholder="상세주소 (동/호수 등) — 직접 입력" data-testid="p9-input-address2"
              className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
            <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
              placeholder="배송 메모 (선택)" rows={2} data-testid="p9-input-memo"
              className="w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent" />
            {me && (
              <label className="text-[11px] inline-flex items-center gap-1.5 text-gray-500">
                <input
                  type="checkbox"
                  checked={saveAsAddress}
                  onChange={(e) => setSaveAsAddress(e.target.checked)}
                  data-testid="p9-save-address"
                />
                이 배송지를 주소록에 저장{addresses.length === 0 ? ' (기본 배송지로 지정)' : ''}
              </label>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 sticky bottom-2">
        <button onClick={cancel} disabled={submitting}
          className="flex-1 py-3 rounded-full text-sm font-black border border-black/10 dark:border-white/10"
          data-testid="p9-cancel-btn">취소</button>
        <button onClick={submit} disabled={submitting}
          className="flex-1 py-3 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-40"
          data-testid="p9-submit-btn">{submitting ? '접수 중…' : '주문 접수'}</button>
      </div>
      <p className="text-[10px] text-center text-gray-400 pt-2">
        결제는 판매자와 별도로 안내드립니다. 접수 후 판매자가 주문을 확인합니다.
      </p>
    </div>
  );
}

// ---- Public status card visible to guests (no test wording exposed) --------
function PublicOrderStatusMini({ orderId }) {
  const [o, setO] = useState(null);
  useEffect(() => {
    if (!orderId) return;
    const load = () => axios.get(`${API}/public/orders/${orderId}`).then((r) => setO(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [orderId]);
  if (!o) return null;
  const pay = o.payment_status || 'pending';
  const del = o.delivery_status || 'pending';
  const stages = [
    { key: 'pending', label: '결제대기', done: true },
    { key: 'paid', label: '결제완료', done: pay === 'paid' || pay === 'refunded' },
    { key: 'preparing', label: '배송준비', done: ['invoice_created', 'pickup_done', 'in_transit', 'delivered'].includes(del) },
    { key: 'in_transit', label: '배송중', done: ['in_transit', 'delivered'].includes(del) },
    { key: 'delivered', label: '배송완료', done: del === 'delivered' },
  ];
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 text-left" data-testid="p9-status-mini">
      <p className="text-[11px] font-black mb-2">주문 상태</p>
      <ol className="grid grid-cols-5 gap-1 text-center">
        {stages.map((s) => (
          <li key={s.key} className={`text-[9px] ${s.done ? 'text-[#FF3B30] font-black' : 'text-gray-400'}`}
            data-testid={`p9-stage-${s.key}`}>
            <div className={`w-4 h-4 rounded-full mx-auto mb-1 ${s.done ? 'bg-[#FF3B30]' : 'bg-gray-200 dark:bg-white/10'}`} />
            {s.label}
          </li>
        ))}
      </ol>
      {pay === 'pending' && o.status !== 'cancelled' && (
        <Link to={`/orders/${orderId}/pay`} data-testid="p9-pay-now"
          className="mt-3 block w-full text-center py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black">결제하기</Link>
      )}
      {o.tracking_number && (
        <p className="text-[10px] text-gray-500 mt-2 text-center">
          송장번호 <span className="font-mono">{o.tracking_number}</span>
          {o.delivery_carrier_name ? ` · ${o.delivery_carrier_name}` : ''}
        </p>
      )}
    </div>
  );
}
